/**
 * MP listing fitment box (Hakon V2 MP / Hakon V2 Kits MP and any product without
 * the halo.customtab fitment metafield). Reads the "Vehicle Application" table that
 * snippets/mp-fitment-box.liquid copies into a <template>, groups the rows, and shows
 * "Fits ..." lines, a "View full fitment" link and a vehicle collection link.
 * Product descriptions are never modified. Wording never says a part doesn't fit.
 * Old Hakon additions (8 Oct 2026): TM vendors' "Application" source as a table OR a <ul> of
 * "MAKE MODEL YYYY-YYYY - CHASSIS" lines (liCells); make aliases for collection matching (VW, Ford Australia...);
 * data-mp-fit-kind="collections" (TM products with no description list: rows built from vehicle collections,
 * 4th cell = collection handle, "View full fitment" opens the Vehicle Fitment tab).
 */
(function (root) {
  'use strict';

  var MAX_LINES = 4;
  var GENERIC = { GTR: 1, NON: 1, HICAS: 1, EVO: 1, BUG: 1, BLOB: 1, HAWK: 1, EYE: 1, AWD: 1, RWD: 1, FWD: 1, ON: 1, SERIES: 1, GEN: 1, INC: 1, TYPE: 1, HATCH: 1, SEDAN: 1, WAGON: 1, UTE: 1, COUPE: 1, TURBO: 1 };

  // Multi-word makes seen in TM Application tables (used to split <li> lines into make / model).
  var MULTI_MAKES = ['FORD AUSTRALIA', 'FORD GERMANY', 'FORD USA', 'FORD UK', 'FORD AU', 'LAND ROVER', 'ALFA ROMEO', 'GREAT WALL', 'ASIA MOTORS', 'ASTON MARTIN', 'ROLLS ROYCE', 'MERCEDES BENZ', 'LEYLAND INNOCENTI'];
  // Description make -> vehicle-data.json make, used only when no exact make exists in the data.
  var MAKE_ALIAS = { vw: 'volkswagen', fordaustralia: 'ford', fordau: 'ford', fordusa: 'ford', fordgermany: 'ford', forduk: 'ford', fpv: 'ford', mercedes: 'mercedesbenz', greatwall: 'gwm', mazdaspeed: 'mazda' };

  function clean(s) { return String(s == null ? '' : s).replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim(); }
  function alnum(s) { return clean(s).toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function titleWord(w) { return /^[A-Z]{4,}$/.test(w) ? w.charAt(0) + w.slice(1).toLowerCase() : w; }
  function niceName(s) { return clean(s).split(' ').map(titleWord).join(' '); }

  function parseYears(cell) {
    var s = clean(cell);
    var m = s.match(/^(\d{4})\s*[-–]\s*(\d{4}|on|present)\b\s*[-–]?\s*(.*)$/i);
    if (m) {
      var to = /^\d{4}$/.test(m[2]) ? parseInt(m[2], 10) : 9999;
      return { from: parseInt(m[1], 10), to: to, chassis: clean(m[3]) };
    }
    m = s.match(/^(\d{4})\b\s*[-–]?\s*(.*)$/);
    if (m) return { from: parseInt(m[1], 10), to: parseInt(m[1], 10), chassis: clean(m[2]) };
    return { from: null, to: null, chassis: s };
  }

  function yearsLabel(r) {
    if (!r.from) return '';
    if (r.to === 9999) return r.from + '–on';
    if (r.to === r.from) return String(r.from);
    return r.from + '–' + r.to;
  }

  /** One <li> line "HOLDEN H SERIES 1971-1976 - H SERIES HQ, HJ" -> [make, model, yearsChassis]. */
  function liCells(text) {
    var s = clean(text);
    if (!s) return null;
    var m = s.match(/\b(\d{4})\s*[-–]\s*(\d{4}|on|present)\b/i) || s.match(/\b(19\d{2}|20\d{2})\b/);
    var pre = m ? clean(s.slice(0, m.index)) : s;
    var yc = m ? clean(s.slice(m.index)) : '';
    var up = pre.toUpperCase(), make = '';
    MULTI_MAKES.forEach(function (mm) { if (!make && (up === mm || up.indexOf(mm + ' ') === 0)) make = pre.slice(0, mm.length); });
    if (!make) make = pre.split(' ')[0];
    var model = clean(pre.slice(make.length));
    return [make, model, yc];
  }

  /** rows: array of [make, model, yearsChassis, (handle)] (strings) */
  function parseRows(cellRows) {
    var out = [];
    cellRows.forEach(function (c) {
      if (!c || c.length < 3) return;
      var make = clean(c[0]), model = clean(c[1]), yc = clean(c[2]);
      if (!make || !model) return;
      if (/^make$/i.test(make) && /^model$/i.test(model)) return;
      var y = parseYears(yc);
      // TM data repeats the model in the chassis ("COMMODORE VF"): drop the repeat
      var mu = model.toUpperCase();
      if (mu && y.chassis.toUpperCase().indexOf(mu + ' ') === 0) y.chassis = clean(y.chassis.slice(mu.length));
      out.push({ make: niceName(make), model: niceName(model), from: y.from, to: y.to, chassis: y.chassis, raw: [make, model, yc], h: c[3] ? clean(c[3]) : '' });
    });
    return out;
  }

  function groupRows(rows) {
    // pass 1: same make + years + chassis -> combine models
    var g1 = [], idx1 = {};
    rows.forEach(function (r) {
      var k = [r.make, r.from, r.to, r.chassis.toUpperCase()].join('|');
      if (!idx1[k]) { idx1[k] = { make: r.make, from: r.from, to: r.to, chassis: [r.chassis], models: [], rows: [] }; g1.push(idx1[k]); }
      if (idx1[k].models.indexOf(r.model) < 0) idx1[k].models.push(r.model);
      idx1[k].rows.push(r);
    });
    // pass 2: same make + years + model set -> combine chassis
    var g2 = [], idx2 = {};
    g1.forEach(function (g) {
      var k = [g.make, g.from, g.to, g.models.join('/')].join('|');
      if (!idx2[k]) { idx2[k] = { make: g.make, from: g.from, to: g.to, models: g.models, chassis: [], rows: [] }; g2.push(idx2[k]); }
      g.chassis.forEach(function (c) { if (c && idx2[k].chassis.indexOf(c) < 0) idx2[k].chassis.push(c); });
      idx2[k].rows = idx2[k].rows.concat(g.rows);
    });
    g2.forEach(function (g) {
      var y = yearsLabel(g);
      g.label = clean(g.make + ' ' + g.models.join(' / ') + ' ' + g.chassis.join(' / ')) + (y ? ' (' + y + ')' : '');
    });
    return g2;
  }

  function tokens(s) {
    return clean(s).toUpperCase().split(/[^A-Z0-9]+/).filter(function (t) {
      return t.length >= 2 && !GENERIC[t] && !/^\d+$/.test(t);
    });
  }

  function vehicleYears(label) {
    var m = String(label || '').match(/(\d{4})\s*-\s*(\d{4}|on)/i);
    if (!m) return null;
    return { from: parseInt(m[1], 10), to: /^\d{4}$/.test(m[2]) ? parseInt(m[2], 10) : 9999 };
  }

  var VARIANT = { sti: 1, gtr: 1, nismo: 1, typer: 1, amg: 1, rs: 1, m: 1 };
  function modelTokens(s) { return clean(s).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean); }

  /** Match one parsed row to a vehicle collection in vehicle-data.json.
   *  Returns {h, u, score} or null (no match, or two collections tie = ambiguous). */
  function matchRow(row, data) {
    if (!data || !data.makes) return null;
    var mk = alnum(row.make), make = null;
    data.makes.forEach(function (m) { if (alnum(m.n) === mk) make = m; });
    if (!make && MAKE_ALIAS[mk]) data.makes.forEach(function (m) { if (alnum(m.n) === MAKE_ALIAS[mk]) make = m; });
    if (!make) return null;
    var rm = alnum(row.model), rFirst = alnum(clean(row.model).split(/[\s(]/)[0]);
    var rmTok = modelTokens(row.model);
    var rTok = tokens(row.chassis);
    var wantsAwd = /\bGTR\b/i.test(row.chassis);
    var best = null, tie = false;
    make.m.forEach(function (md) {
      var vm = alnum(md.n);
      var exact = vm === rm || vm === rFirst;
      var modelOk = exact || (Math.min(vm.length, rm.length) >= 2 && (vm.indexOf(rm) >= 0 || rm.indexOf(vm) >= 0));
      var variantPenalty = modelTokens(md.n).filter(function (t) { return VARIANT[t] && rmTok.indexOf(t) < 0; }).length * 2;
      md.v.forEach(function (v) {
        var labelNoYears = String(v.l || '').replace(/\d{4}\s*-\s*(\d{4}|on)/ig, ' ');
        var lTok = tokens(labelNoYears);
        var vTok = lTok.concat(tokens(String(v.h || '').replace(/-/g, ' ')));
        var overlap = rTok.filter(function (t) {
          return vTok.some(function (u) { return u === t || (Math.min(u.length, t.length) >= 3 && (u.slice(-t.length) === t || t.slice(-u.length) === u)); });
        }).length;
        // both sides name a chassis code but none in common: different generation, never match on years alone
        if (rTok.length && lTok.length && overlap === 0) return;
        var vy = vehicleYears(v.l), yOverlap = false, yExact = false;
        if (vy && row.from) { yOverlap = row.from <= vy.to && vy.from <= row.to; yExact = row.from === vy.from; }
        var ok = (modelOk && (overlap > 0 || yOverlap)) || (overlap > 0 && yOverlap);
        if (!ok) return;
        var score = (modelOk ? 4 : 0) + (exact ? 1 : 0) - variantPenalty + overlap * 3 + (yOverlap ? 1 : 0) + (yExact ? 1 : 0) +
          (wantsAwd && /\bAWD\b/i.test(v.l) ? 1 : 0);
        if (!best || score > best.score) { best = { h: v.h, u: v.u, score: score }; tie = false; }
        else if (score === best.score && v.h !== best.h) { tie = true; }
      });
    });
    return best && !tie ? best : null;
  }

  /** How strongly a row matches the listing title (one MP listing per vehicle). */
  function titleRowScore(row, title) {
    if (!title) return 0;
    var T = ' ' + clean(title).toUpperCase().replace(/[^A-Z0-9]+/g, ' ') + ' ';
    var tA = alnum(title);
    var modelHit = alnum(row.model).length >= 2 && tA.indexOf(alnum(row.model)) >= 0;
    if (!modelHit) {
      var mw = clean(row.model).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
      modelHit = mw.length > 0 && mw.every(function (w) { return T.indexOf(' ' + w + ' ') >= 0; });
    }
    var ch = tokens(row.chassis).filter(function (t) { return T.indexOf(' ' + t + ' ') >= 0; }).length;
    var chA = alnum(row.chassis);
    if (!ch && chA.length >= 4 && tA.indexOf(chA) >= 0) ch = 1; // e.g. 'EVO 7 8 9'
    var makeHit = T.indexOf(' ' + clean(row.make).toUpperCase() + ' ') >= 0;
    return (modelHit ? 2 : 0) + ch * 3 + (makeHit ? 1 : 0);
  }

  /** Put the group that matches the listing title first. Returns {groups, primary}. */
  function orderByTitle(groups, title) {
    var bestI = -1, bestS = 0, tie = false;
    groups.forEach(function (g, i) {
      var sc = 0;
      g.rows.forEach(function (r) { sc = Math.max(sc, titleRowScore(r, title)); });
      g.titleScore = sc;
      if (sc > bestS) { bestS = sc; bestI = i; tie = false; } else if (sc === bestS && sc > 0) { tie = true; }
    });
    if (bestI < 0 || bestS < 3 || tie) return { groups: groups, primary: null };
    var primary = groups[bestI];
    return { groups: [primary].concat(groups.filter(function (g, i) { return i !== bestI; })), primary: primary };
  }

  /** Decide the "Different car?" link: one unambiguous vehicle collection, else the homepage finder.
   *  Uses the title vehicle's rows when the listing title names one of the table vehicles. */
  function pickCollection(rows, data, primary, title) {
    var use = rows;
    if (primary) {
      use = primary.rows;
      var named = use.filter(function (r) { return titleRowScore(r, title) >= 2 && alnum(title).indexOf(alnum(r.model)) >= 0; });
      if (named.length) {
        var longest = Math.max.apply(null, named.map(function (r) { return alnum(r.model).length; }));
        use = named.filter(function (r) { return alnum(r.model).length === longest; });
      }
    }
    var handles = [], matches = [];
    use.forEach(function (r) {
      var m = matchRow(r, data);
      matches.push(m);
      if (m && handles.indexOf(m.h) < 0) handles.push(m.h);
    });
    var url = null;
    if (handles.length === 1) {
      var m0 = matches.filter(Boolean)[0];
      url = m0.u || ((data.url_prefix || '/collections/') + m0.h);
    }
    return { url: url, handles: handles, matches: matches, rows: use };
  }

  function el(tag, cls, text) { var e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }

  var TICK = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" focusable="false" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  function findDescriptionHeading() {
    var nodes = document.querySelectorAll('.card--product-description .rte p, .card--product-description .rte strong, .card--product-description .rte h2, .card--product-description .rte h3, .card--product-description .rte h4');
    for (var i = 0; i < nodes.length; i++) {
      if (/^\s*(vehicle )?application\s*:?\s*$/i.test(nodes[i].textContent || '')) {
        var n = nodes[i];
        while (n.parentElement && n.parentElement.tagName !== 'DIV' && !n.parentElement.classList.contains('rte')) n = n.parentElement;
        return n;
      }
    }
    return null;
  }

  function revealAndScroll(target) {
    // open collapsed description containers first
    var exp = target.closest('.expandable-content[aria-expanded="false"]');
    if (exp) { var t = exp.querySelector('.expandable-content__toggle'); if (t) t.click(); }
    var coll = target.closest('.card__collapsible');
    if (coll) { var btn = document.querySelector('[aria-controls="' + coll.id + '"][aria-expanded="false"]'); if (btn) btn.click(); }
    var panel = target.closest('[data-product-panel][hidden]');
    if (panel) { var tab = document.querySelector('[data-product-tab="' + panel.getAttribute('data-product-panel') + '"]'); if (tab) tab.click(); }
    setTimeout(function () {
      var header = document.querySelector('.shopify-section--header, #shopify-section-header, [id*="__header"]');
      var off = header ? Math.min(header.getBoundingClientRect().height, 160) : 0;
      var y = target.getBoundingClientRect().top + window.pageYOffset - off - 16;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      var table = target.nextElementSibling && /^(TABLE|UL)$/.test(target.nextElementSibling.tagName) ? target.nextElementSibling : null;
      [target, table].forEach(function (e) { if (!e) return; e.classList.add('mp-fit-flash'); setTimeout(function () { e.classList.remove('mp-fit-flash'); }, 1800); });
    }, exp || coll || panel ? 250 : 0);
  }

  function render(box, data) {
    var tpl = box.querySelector('template[data-mp-fit-source]');
    if (!tpl) return;
    var frag = tpl.content || tpl;
    var cellRows = [];
    frag.querySelectorAll('tr').forEach(function (tr) {
      var cells = tr.querySelectorAll('td, th');
      cellRows.push(Array.prototype.map.call(cells, function (c) { return c.textContent; }));
    });
    if (!cellRows.length) frag.querySelectorAll('li').forEach(function (li) { var c = liCells(li.textContent); if (c) cellRows.push(c); });
    var rows = parseRows(cellRows);
    if (!rows.length) {
      // nothing usable (e.g. list lines without a make): hide the panel AND the product finder bar (they go together)
      box.hidden = true;
      document.querySelectorAll('.vf-section--product').forEach(function (s) { s.hidden = true; });
      return;
    }
    var title = box.getAttribute('data-product-title') || '';
    var ordered = orderByTitle(groupRows(rows), title);
    var groups = ordered.groups;
    box._mpFit = { rows: rows, primary: ordered.primary, title: title };
    var wrap = box.querySelector('[data-mp-fit-lines]');
    wrap.innerHTML = '';
    function line(text, cls) {
      var li = el('li', 'mp-fit__line' + (cls ? ' ' + cls : ''));
      var tick = el('span', 'mp-fit__tick'); tick.innerHTML = TICK;
      li.appendChild(tick);
      var span = el('span', 'mp-fit__text');
      var m = text.match(/^(.*\S)\s+(\([^()]*\))$/);
      if (m) { span.appendChild(document.createTextNode(m[1] + ' ')); span.appendChild(el('span', 'mp-fit__years', m[2])); }
      else span.textContent = text;
      li.appendChild(span);
      return li;
    }
    var shown = 0, list;
    if (ordered.primary || groups.length === 1) {
      list = el('ul', 'mp-fit__lines'); list.setAttribute('role', 'list');
      list.appendChild(line('Fits ' + groups[0].label, 'mp-fit__line--primary'));
      wrap.appendChild(list); shown = 1;
      if (groups.length > 1) wrap.appendChild(el('p', 'mp-fit__intro', 'Also fits:'));
    } else {
      wrap.appendChild(el('p', 'mp-fit__intro', 'Fits these vehicles:'));
    }
    if (groups.length > shown) {
      list = el('ul', 'mp-fit__lines'); list.setAttribute('role', 'list');
      groups.slice(shown, MAX_LINES).forEach(function (g) { list.appendChild(line(g.label)); });
      wrap.appendChild(list);
    }
    if (groups.length > MAX_LINES) {
      var more = el('a', 'mp-fit__more', 'and ' + (groups.length - MAX_LINES) + ' more');
      more.href = '#mp-vehicle-application';
      more.setAttribute('data-mp-fit-view', '');
      wrap.appendChild(more);
    }
    box.hidden = false;
    box.setAttribute('data-mp-fit-groups', groups.length);

    var heading = findDescriptionHeading();
    // collection-driven panel (no description list): "View full fitment" opens the Vehicle Fitment tab
    if (!heading && box.getAttribute('data-mp-fit-kind') === 'collections') heading = document.querySelector('[data-product-panel="fitment"]');
    if (heading && !document.getElementById('mp-vehicle-application')) heading.id = 'mp-vehicle-application';
    box.querySelectorAll('[data-mp-fit-view]').forEach(function (a) {
      if (!heading) { a.href = (box.getAttribute('data-product-url') || '') + '#mp-vehicle-application'; return; }
      a.addEventListener('click', function (e) { e.preventDefault(); revealAndScroll(heading); });
    });

  }

  /** "Different car?" -> the compact finder bar on this page (MP product pages), else the homepage finder. */
  function wireFinderLink(box) {
    var other = box.querySelector('[data-mp-fit-other]');
    if (!other || other._mpWired) return;
    other._mpWired = true;
    other.addEventListener('click', function (e) {
      var vf = document.querySelector('vehicle-filter[data-mode="product"]');
      if (!vf) return; // keep the homepage /#vehicle-finder link
      e.preventDefault();
      var header = document.querySelector('.shopify-section--header, #shopify-section-header, [id*="__header"]');
      var sticky = header && /sticky/.test(getComputedStyle(header).position) ? header.getBoundingClientRect().height : 0;
      var y = vf.getBoundingClientRect().top + window.pageYOffset - sticky - 12;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      if (typeof vf.setOpen === 'function') vf.setOpen(true);
      var make = vf.querySelector('[data-vf-level="make"]');
      setTimeout(function () { if (make) make.focus({ preventScroll: true }); }, 450);
      vf.classList.add('mp-fit-flash');
      setTimeout(function () { vf.classList.remove('mp-fit-flash'); }, 1800);
    });
  }

  var dataPromise = null;
  function loadData(url) {
    if (!url || !root.fetch) return Promise.resolve(null);
    if (!dataPromise) dataPromise = fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    return dataPromise;
  }

  function init() {
    document.querySelectorAll('[data-mp-fit]:not([data-mp-fit-ready])').forEach(function (box) {
      box.setAttribute('data-mp-fit-ready', '');
      try { render(box, null); wireFinderLink(box); } catch (e) { if (root.console) console.warn('mp-fitment', e); }
      loadData(box.getAttribute('data-vehicle-data')).then(function (data) {
        if (!data) return;
        try {
          var st = box._mpFit;
          if (!st) return;
          var pick = pickCollection(st.rows, data, st.primary, st.title);
          if (box.getAttribute('data-mp-fit-kind') === 'collections') {
            // rows ARE the product's vehicle collections: use their handles directly
            var hs = [];
            (st.primary ? st.primary.rows : st.rows).forEach(function (r) { if (r.h && hs.indexOf(r.h) < 0) hs.push(r.h); });
            pick = { handles: hs, url: hs.length === 1 ? (data.url_prefix || '/collections/') + hs[0] : null };
          }
          var shop = box.querySelector('[data-mp-fit-shop]');
          if (pick.url && pick.handles.length === 1) {
            if (shop) { shop.href = pick.url; shop.hidden = false; shop.setAttribute('data-mp-fit-match', pick.handles[0]); }
            document.querySelectorAll('vehicle-filter[data-mode="product"]').forEach(function (vf) { vf.setAttribute('data-preselect', pick.handles[0]); });
            document.dispatchEvent(new CustomEvent('mp-fitment:match', { detail: { h: pick.handles[0], url: pick.url } }));
          } else if (shop) { shop.hidden = true; }
        } catch (e) { if (root.console) console.warn('mp-fitment', e); }
      });
    });
  }

  root.HakonMpFitment = { liCells: liCells, parseRows: parseRows, groupRows: groupRows, orderByTitle: orderByTitle, titleRowScore: titleRowScore, matchRow: matchRow, pickCollection: pickCollection, init: init };
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
    document.addEventListener('shopify:section:load', init);
  }
})(typeof window !== 'undefined' ? window : globalThis);
